import React, {forwardRef} from 'react';

import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Info, type InfoProps} from '../../../icons/Info';
import {Label, P} from '../../../layout/Text/Text';
import {InfoPopup} from '../../InfoPopup/InfoPopup';
import {Hints} from '../Hints/Hints';

import {useTextareaField} from './useTextareaField';
import {
    getBaseVariables,
    getDisabledVariables,
    getErrorVariables,
    getFocusVariables
} from './utils';

import type {ValidationFunc} from '@nfq/react-form';

/**
 * The `ComponentProps` interface defines the props for input and textarea field components.
 * These props include fields such as `initialValue`, `label`, `name`, and optional callbacks for handling changes.
 * This interface allows for customizable placeholders, input styles, and supports test identifiers for testing purposes.
 */
export interface ComponentProps {
    /**
     * The height of the input field is either static or dynamic.
     * When true, the textarea grows with its content until it reaches a max height.
     *
     * @default false
     */
    dynamicHeight?: boolean;
    /**
     * An array of hint messages to display below the dropdown input.
     * These hints provide additional context or guidance to the user about the expected input
     * or usage of the dropdown component.
     *
     * @default undefined
     */
    hints?: string[];
    /**
     * The initial value of the input field.
     * This value is set when the input is first rendered.
     *
     * @default ''
     */
    initialValue?: string;
    /**
     * Determines whether the dropdown input should be rendered in a disabled state.
     * When disabled, the dropdown becomes non-interactive and displays visual indicators
     * to show that it cannot be used for selection.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    keepOnUnmount?: boolean;
    /**
     * The label for the input field.
     * This label is displayed above the input element.
     */
    label?: string;
    /**
     * The name attribute for the input field.
     * It identifies the input element in form submissions and the form state.
     */
    name: string;
    /**
     * Callback function triggered when the input value changes.
     */
    onChange?(value: string): void;
    /**
     * The placeholder text shown inside the input when it is empty.
     * Provides guidance on what to input.
     */
    placeholder?: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'InputField'
     */
    testId?: string;
    tooltipText?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * The `TextareaField` component renders a styled textarea input field within a form.
 * It accepts various props to manage its appearance and behavior, including initial values, labels, and styles.
 * The component integrates with form state through `useFormField` and supports custom change handling.
 *
 * @param props              The component props.
 * @param props.initialValue The initial value of the textarea field. Defaults to an empty string.
 * @param props.label        The label displayed above the textarea field. If not provided, no label will be shown.
 * @param props.name         The name of the textarea field, used to track the form state.
 * @param props.onChange     The callback function triggered when the textarea value changes.
 * @param props.placeholder  The placeholder text displayed when the textarea is empty.
 * @param props.style        The style theme of the textarea field, either 'dark' or 'light'. Defaults to 'dark'.
 * @param props.testId       The unique identifier for testing purposes. Defaults to 'TextareaField'.
 * @returns A JSX element that renders a labeled textarea field with optional placeholder text and customizable styling.
 *
 * @example
 * ```tsx
 * <TextareaField
 *   dynamicHeight=false
 *   initialValue="Initial text"
 *   label="Comment"
 *   name="comment"
 *   placeholder="Write your comment here"
 *   onChange={(val) => console.log(val)}
 *   style="light"
 *   testId="comment-field"
 * />
 * ```
 */
const TextareaField = observer(forwardRef<HTMLTextAreaElement, ComponentProps>(({
    dynamicHeight = false,
    hints = [],
    initialValue = '',
    isDisabled = false,
    keepOnUnmount = false,
    label,
    name,
    onChange = () => {},
    placeholder,
    testId = 'TextareaField',
    tooltipText,
    validation = () => null

}, ref) => {
    const {colors, error, handleChange, value} = useTextareaField(
        name,
        initialValue,
        keepOnUnmount,
        validation,
        onChange
    );

    return (
        <Wrapper>
            <StyledLabel>
                <LabelInfoButtonWrapper>
                    <InputLabel data-cy={`${testId}-label`}>{label}</InputLabel>
                    {tooltipText && (
                        <InfoPopup testId={`${testId}-info-popup`}>
                            <P $size="small" data-cy={`${testId}-info-text`}>{tooltipText}</P>
                        </InfoPopup>
                    )}
                </LabelInfoButtonWrapper>
                <Spacer y={2} />
                <InputErrorIconWrapper>
                    <TextArea
                        ref={ref}
                        $dynamicHeight={dynamicHeight}
                        data-cy={`${testId}-input`}
                        data-has-error={error !== null && error.length > 0}
                        disabled={isDisabled}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                    />
                    <ErrorIcon $show={(error !== null && error.length > 0)} color1={colors.error} testId={`${testId}-error-icon`} />
                </InputErrorIconWrapper>
            </StyledLabel>
            {(error !== null && error.length > 0) && (
                <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
            )}
            {(error === null || error.length === 0) && <Hints baseTestId={testId} hints={hints} paddingLeft={0} />}
        </Wrapper>

    );
}));

TextareaField.displayName = 'TextareaField';

export {TextareaField};

const Wrapper = styled.div`
    ${getBaseVariables};

    &:has(textarea:focus-visible) {
        ${getFocusVariables};
    }

    &:has(textarea[data-has-error=true]) {
        ${getErrorVariables}
    }

    &:has(textarea[disabled]) {
        ${getDisabledVariables};
    }
`;

const StyledLabel = styled(Label)`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const LabelInfoButtonWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
`;

const InputLabel = styled(P)`
    color: var(--label-font-color);
    transition: color 0.3s ease-in-out;
`;

const InputErrorIconWrapper = styled.div`
    position: relative;
    width: 100%;
`;

interface TextAreaProps {
    $dynamicHeight: boolean;
}

const TextArea = styled.textarea<TextAreaProps>`
    background-color: var(--textarea-background-color);
    border: 1px solid var(--textarea-border-color);
    border-radius: 4px;
    color: var(--textarea-font-color);
    font-family: ${({theme}) => theme.fonts.OnAir};
    font-size: 1.6rem;
    line-height: 2.4rem;
    min-height: 12rem;
    ${({$dynamicHeight}) => ($dynamicHeight ? 'field-sizing: content' : 'height: 100%')};
    padding: ${spacing(3)} ${spacing(4)};
    ${({$dynamicHeight}) => ($dynamicHeight ? 'max-height: 30rem' : null)};
    resize: vertical;
    transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out;
    width: 100%;
    word-break: break-word;

    &:placeholder {
        color: var(--textarea-placeholder-font-color);
    }

    &:focus {
        outline: none;
    }

    &[disabled] {
        cursor: not-allowed;
    }
`;

interface ErrorIconProps extends InfoProps {
    $show: boolean;
}

const ErrorIcon = styled(Info)<ErrorIconProps>`
    opacity: ${({$show}) => ($show ? 1 : 0)};
    position: absolute;
    right: ${spacing(4)};
    top: ${spacing(3)};
    transition: opacity 0.3s ease-in-out;
`;